import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { verifyTotpSchema, type VerifyTotpInput } from '@bifeai/shared';
import { Button, Callout, Field, Input, Spinner } from '@bifeai/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { reloadMe, useMeQuery } from '../../lib/session';
import { AuthLayout } from './AuthLayout';

export function VerifyCodePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMeQuery();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VerifyTotpInput>({ resolver: zodResolver(verifyTotpSchema) });

  if (me.isPending)
    return (
      <div className="bf-center-screen">
        <Spinner />
      </div>
    );
  if (me.isError) return <Navigate to="/ingresar" replace />;
  if (me.data.session.mfaVerified) return <Navigate to="/" replace />;
  if (me.data.session.mfaSetupRequired) return <Navigate to="/activar-2fa" replace />;

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.post('/auth/2fa/verify', values);
      await reloadMe(qc);
      navigate(me.data.user.mustChangePassword ? '/cambiar-clave' : '/', {
        replace: true,
      });
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <AuthLayout title="Código de seguridad" subtitle="Abrí tu app de autenticación e ingresá el código de 6 números de BifeAI.">
      <form className="bf-form" onSubmit={onSubmit} noValidate>
        {formError && <Callout tone="danger">{formError}</Callout>}
        <Field label="Código" error={errors.code?.message}>
          <Input
            className="bf-code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            placeholder="000000"
            {...register('code')}
          />
        </Field>
        <Button type="submit" variant="primary" block loading={isSubmitting}>
          Verificar
        </Button>
        <Button variant="ghost" block onClick={() => void api.post('/auth/logout').finally(() => navigate('/ingresar'))}>
          Ingresar con otra cuenta
        </Button>
      </form>
    </AuthLayout>
  );
}
